// @ts-check
/**
 * app.
 * @typedef {Object} AppState
 * @prop {string} canvasId
 */

/**
 * @typedef {'photo-print' | 'canvas' | 'mask' | 'poster' | 'calendar' | 'photo-book'} ModeType
 */

/**
 * @typedef {Object} OnboardingState
 * @property {boolean} completed
 * @prop {number} step
 */

/**
 * @typedef {Object} ModalState
 * @property {boolean} isOpen
 * @property {any} modalProps
 * @property {boolean} dismissable
 * @prop {string} type
 */

/**
 * @typedef {Object} CalendarPageDate
 * @property {number} year
 * @property {number} month Zero-based month index, matching JavaScript Date.
 */

/**
 * @typedef {Object} EventType
 * @property {number} id
 * @property {number} year
 * @property {number} day
 * @property {number} month
 * @property {string} event
 * @property {any} data
 * @property {boolean} defaultEvent
 * @property {boolean} isActive
 */

/**
 * @typedef {Object} DimensionsType
 * @property {number} halfBleed
 * @property {number} frame
 * @property {number} border
 * @property {number} retroBorder
 * @property {number} retroBottom
 * @property {number} width
 * @property {number} height
 * @property {number} [originalFrame]
 */

/**
 * @typedef {Object} LayoutItemType
 * @property {number} height
 * @property {number} width
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {Object} LayoutType
 * @property {string} id
 * @property {string} label
 * @property {boolean} default
 * @property {number} margin
 * @property {LayoutItemType[]} layout
 */

/**
 * @typedef {Object} ShapeImage
 * @property {number} id - The unique identifier of the image.
 * @property {number} x - The x coordinate of the top-left corner of the image.
 * @property {number} y - The y coordinate of the top-left corner of the image.
 * @property {number} width - The width of the image.
 * @property {number} height - The height of the image.
 * @property {number} brightness - The brightness of the image.
 * @property {number} contrast - The contrast of the image.
 * @property {number} saturation - The saturation of the image.
 * @property {boolean} initialized - Whether the image has been initialized.
 * @property {number} naturalHeight - The natural height of the image.
 * @property {number} naturalWidth - The natural width of the image.
 * @property {number} centerX - The x coordinate of the center of the image.
 * @property {number} centerY - The y coordinate of the center of the image.
 * @property {number} minWidth - The minimum width of the image.
 * @property {number} minHeight - The minimum height of the image.
 * @property {number} offsetX - The x offset of the image.
 * @property {number} offsetY - The y offset of the image.
 */

/** @typedef {1|2} CalendarType */

/**
 * Default typography applied to event text inside calendar day cells.
 * @typedef {Object} CalendarEventStyle
 * @property {number} fontSize - Shared event font size in calendar SVG units, clamped to the largest value that fits every populated day cell.
 * @property {string} fontFamily - Font family used to render event text.
 * @property {boolean} isBold - Whether event text is rendered with a bold font weight.
 * @property {boolean} isItalic - Whether event text is rendered in italic style.
 * @property {string} fill - Event text colour as a CSS-compatible colour value.
 */

/**
 * @typedef {Object} CalendarDay
 * @property {string} day Date key in `dd.MM.yyyy` format.
 * @property {Shape[]} shapes Text and other shapes rendered in this calendar day.
 */

/**
 * @typedef {Object} Shape
 * @property {string} [id] - The unique identifier of the shape.
 * @property {number} x - The x coordinate of the top-left corner of the shape.
 * @property {number} y - The y coordinate of the top-left corner of the shape.
 * @property {number} width - The width of the shape.
 * @property {number} height - The height of the shape.
 * @property {string} [type] - The type of the shape.
 * @property {(ShapeImage | null)} [image] - The image associated with the shape, or null if there is no image.
 * @property {number} [rotation] - The rotation angle of the shape in degrees.
 * @property {string} [text]
 * @property {number} [centerX]
 * @property {number} [centerY]
 * @property {number} [minWidth]
 * @property {number} [minHeight]
 * @property {number} [offsetX]
 * @property {number} [offsetY]
 * @property {string} [fontFamily]
 * @property {string} [mainColor]
 * @property {string} [secondaryColor]
 * @property {string} [otherMonthColor] - Optional date-number colour for days outside the current month.
 * @property {string} [weekendColor] - Optional date-number colour for Saturdays and Sundays.
 * @property {boolean} [useOtherMonthColor] - Whether otherMonthColor is applied. Missing values preserve the legacy enabled behaviour.
 * @property {boolean} [useWeekendColor] - Whether weekendColor is applied after and overrides the month colours.
 * @property {boolean} [showOtherMonthDays] - Shows adjacent-month dates and events, default true; cell borders remain visible.
 * @property {boolean} [useWeekendEventColor] - Enables a separate event text colour on Saturday and Sunday.
 * @property {string} [weekendEventColor] - Weekend event text colour, independent of date numbers.
 * @property {number} [eventDateSpacing] - Nonnegative gap below date numbers in calendar SVG units, default zero.
 * @property {string} [backgroundColor]
 * @property {string} [borderColor]
 * @property {boolean} [isBold]
 * @property {boolean} [isItalic]
 * @property {CalendarDay[]} [days]
 * @property {number} [dayFontSize]
 * @property {'left'|'center'|'right'} [dayNumberTextAlign] - Date-number alignment. Defaults to right for a month grid and center for a two-row strip.
 * @property {'left'|'center'|'right'} [weekdayNameTextAlign]
 * @property {import('constants/calendarSettings').Margin} [weekdayNamePadding] - Always-applied left, right, top and bottom padding around each weekday name in calendar SVG units. Missing values are derived from the original header layout, with the default top rounded to a whole number.
 * @property {boolean} [borderTop]
 * @property {boolean} [borderRight] - Draws the right day-cell border. Defaults to false.
 * @property {boolean} [borderBottom] - Draws the bottom day-cell border. Defaults to false.
 * @property {boolean} [borderLeft] - Draws the left day-cell border. Defaults to false.
 * @property {string} [dayFormat]
 * @property {number} [headerFontSize] - Applied weekday-name font size, reduced to fit all seven labels after their font loads.
 * @property {boolean} [headerIsBold]
 * @property {boolean} [headerIsItalic]
 * @property {string} [headerFontFamily]
 * @property {string} [headerColor]
 * @property {CalendarType} [calendarType]
 * @property {import('constants/calendarSettings').CalendarShapeSize} [calendarShapeSize]
 * @property {CalendarEventStyle} [eventStyle] - Default typography for event text in calendar cells.
 * @property {boolean} [showDebugGuides] - Whether the admin-only dashed calendar boundary is visible.
 * @property {boolean} [showGridSpacingDebugGuides] - Shows admin-only dashed day-cell content bounds after grid spacing, independently of showDebugGuides. Defaults to false.
 * @property {boolean} [showWeekdayNameDebugGuides] - Shows admin-only dashed weekday-name bounds after padding, independently of showDebugGuides. Defaults to false.
 */

/**
 * Normalized calendar shape used by calendar controls and rendering.
 * These fields are always populated by calendar creation and version migrations.
 * @typedef {Shape & {
 *   id: string,
 *   type: 'calendar',
 *   backgroundColor: string,
 *   mainColor: string,
 *   secondaryColor: string,
 *   borderColor: string,
 *   fontFamily: string,
 *   dayFontSize: number,
 *   isBold: boolean,
 *   isItalic: boolean,
 *   dayFormat: string,
 *   headerFontSize: number,
 *   headerFontFamily: string,
 *   headerIsBold: boolean,
 *   headerIsItalic: boolean,
 *   headerColor: string,
 *   borderTop: boolean,
 *   borderRight: boolean,
 *   borderBottom: boolean,
 *   borderLeft: boolean,
 *   calendarType: CalendarType,
 *   eventStyle: CalendarEventStyle,
 *   days: CalendarDay[]
 * }} CalendarShape
 */

/**
 * A partial update for the calendar's dimensions and cell spacing.
 * @typedef {Object} CalendarShapeSizeUpdate
 * @property {number} [padding]
 * @property {number} [paddingLeft]
 * @property {number} [paddingRight]
 * @property {number} [paddingTop]
 * @property {number} [paddingBottom]
 * @property {number} [width]
 * @property {number} [height]
 * @property {Partial<import('constants/calendarSettings').Margin>} [dayMargin]
 * @property {Partial<import('constants/calendarSettings').Margin>} [dayPadding]
 */

/**
 * Fields accepted when updating a calendar shape from its controls.
 * Nested settings are partial because each control updates only one field.
 * @typedef {Object} CalendarShapeUpdate
 * @property {string} [id]
 * @property {CalendarType} [calendarType]
 * @property {string} [backgroundColor]
 * @property {CalendarShapeSizeUpdate} [calendarShapeSize]
 * @property {string} [dayFormat]
 * @property {'left'|'center'|'right'} [dayNumberTextAlign]
 * @property {'left'|'center'|'right'} [weekdayNameTextAlign]
 * @property {Partial<import('constants/calendarSettings').Margin>} [weekdayNamePadding]
 * @property {number} [dayFontSize]
 * @property {string} [fontFamily]
 * @property {boolean} [isBold]
 * @property {boolean} [isItalic]
 * @property {string} [mainColor]
 * @property {string} [otherMonthColor]
 * @property {string} [weekendColor]
 * @property {boolean} [useOtherMonthColor]
 * @property {boolean} [useWeekendColor]
 * @property {boolean} [showOtherMonthDays]
 * @property {boolean} [useWeekendEventColor]
 * @property {string} [weekendEventColor]
 * @property {number} [eventDateSpacing]
 * @property {number} [headerFontSize]
 * @property {string} [headerFontFamily]
 * @property {boolean} [headerIsBold]
 * @property {boolean} [headerIsItalic]
 * @property {string} [headerColor]
 * @property {Partial<CalendarEventStyle>} [eventStyle]
 * @property {string} [borderColor]
 * @property {string} [secondaryColor]
 * @property {boolean} [borderTop]
 * @property {boolean} [borderRight]
 * @property {boolean} [borderBottom]
 * @property {boolean} [borderLeft]
 * @property {boolean} [showDebugGuides]
 * @property {boolean} [showGridSpacingDebugGuides]
 * @property {boolean} [showWeekdayNameDebugGuides]
 */

/** @typedef {(modifier: CalendarShapeUpdate) => void} CalendarShapeChangeHandler */

/** @type {Shape} */
export const emptyShape = {
  id: '',
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  rotation: 0,
  type: '',
  image: undefined,
};

/**
 * @typedef {Object} ScenePage
 * @property {string} id - The unique identifier of the page configuration.
 * @property {number} [quantity]
 * @property {CalendarPageDate} [date]
 * @property {Shape[]} shapes - An array of Shape objects that define the shapes on the page.
 * @property {Object} config - The configuration settings for the page.
 */

/**
 * @typedef {Object} BackgroundOption
 * @property {string} value - The background colour.
 * @property {string} textColor - text colour
 * @property {boolean} default
 */

/**
 * @typedef {Object} SceneConfigType
 * @property {string} axisColor
 * @property {boolean} addFixedFooterText
 * @property {string} currentPage
 * @property {boolean} viewAllPages
 * @property {boolean} isEditing
 * @prop {string} clippingMaskUrl
 * @prop {string} underlayUrl
 * @property {number} contentOffsetY
 * @property {DimensionsType} dimensions
 * @property {string} edgeWrap
 * @property {string} orientation
 * @property {boolean} initialized
 * @property {ModeType} mode
 * @property {BackgroundOption} currentBackground
 * @property {boolean} admin
 * @property {boolean} disallowOrderWithoutImages - prevents ordering when some pages have no photos
 * @property {LayoutType[]} layouts
 * @property {string} safeMarginText
 * @property {boolean} isLayoutChanged
 */

/**
 * @typedef {Object} SceneState
 * @prop {(Shape | null)} clipboard
 * @property {SceneConfigType} config
 * @property {any} state
 * @property {ScenePage[]} pages
 * @property {EventType[]} events
 */

/**
 * Main store state.
 * @typedef {Object} StoreState
 * @property {AppState} app
 * @property {OnboardingState} onboarding
 * @property {SceneState} scene
 * @property {Object} photos
 * @property {Object} config
 * @property {Object} toolbar
 * @property {ModalState} modal
 */
